import { DatePipe } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { CallSetProfile, DialerService, DialerTestService, RootTest, VoiceCall, VoiceCallTest } from 'dialer';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { asyncScheduler, from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppState, ShowRequest } from '../../../core';
import { DriveTestPointSeq, PathTestPointSeq, TaskTypeName } from '../../../core/models';
import { PathTestService } from '../../../core/services/path-test.service';
import { UserService } from '../../../core/services/user.service';
import { TableViewQueryCriteria } from '../../../shared/components/table-view/table-view-component-base';
import { DialerClientIssueModalComponent } from '../../dialer-client-issue-modal/dialer-client-issue-modal.component';
import { DialerClientService } from '../../../core/services';

@Component({
  selector: 'app-dialer-client-testseq-modal',
  templateUrl: './dialer-client-testseq-modal.component.html',
  styleUrls: ['./dialer-client-testseq-modal.component.scss']
})
export class DialerClientTestSeqModalComponent implements OnInit{

  static MODAL_ID = 9999000500

  static showModal( modalService: BsModalService, initialState: Partial<DialerClientTestSeqModalComponent> ) {
    return modalService.show(DialerClientTestSeqModalComponent, {
      ignoreBackdropClick: true,
      initialState,
      class: 'dialer-client-testseq-modal full-screen-modal',
      id: DialerClientTestSeqModalComponent.MODAL_ID
    })
  }

  @Input() seqContext: {seq: PathTestPointSeq | DriveTestPointSeq, type: TaskTypeName};

  @Input() activeTab: string

  //TODO - make a user name Pipe!!!
  seqUserName$: Observable<string>
  dataUserName$: Observable<string>
  rootTest: RootTest
  calls: VoiceCall[] = []
  callTests: VoiceCallTest[] = []
  showDbgBtns = false;
  testProfile: CallSetProfile&{earlyDisconnectToleranceSecs?: number; earlyDisconnectCarrierRegex?: string};

  constructor(
    public userService: UserService,
    public dialerService: DialerService,
    public dialerTestService: DialerTestService,
    public dialerClientService: DialerClientService,
    public pathTestService: PathTestService,
    protected appState: AppState,
    protected datePipe: DatePipe,
    protected bsModalService: BsModalService,
    protected modalRef: BsModalRef ) {
  }

  ngOnInit() {
    const seq = this.seqContext.seq
    if ( seq?.userId ) {
      this.seqUserName$ = from(this.userService.getById(seq.userId))
          .pipe(map(user=>user?`${user.firstName} ${user.lastName}`:''))
    }
    if ( seq?.data?.userId ) {
      this.dataUserName$ = from(this.userService.getById(seq.data.userId))
          .pipe(map(user=>user?`${user.firstName} ${user.lastName}`:''))
    }
  }

  onCancel() {
    this.modalRef.hide()
  }

  rootTestChanged( rootTest: RootTest ) {
    asyncScheduler.schedule( () => {
      this.rootTest = rootTest
      this.calls = []
      this.callTests = []
      if ( rootTest ) {
        this.dialerTestService.forSelfAndChildren(this.rootTest, test => {
          if ( test.type == 'VoiceCallTest'){
            const callTest = test as VoiceCallTest
            this.callTests.push(callTest)
            const call = callTest?.call
            if ( call ) {
              call['phoneMac'] = callTest.phoneMacAddress
              call['phoneName'] = callTest.phone?.modemProps?.name
              this.calls.push( call )
            }
          }
        });
        this.testProfile = this.dialerClientService.getTestProfile(this.rootTest);
      }
    })
  }

  showDialerCallLog() {
    const queryForSeqId : TableViewQueryCriteria = {
      filters: [ {
        key: 'seqid',
        value: this.rootTest.seqId,
        mode: '='
      }],
      sorts: [
        {
          key: 'starttime',
          order: 'DESC'
        }
      ]
    };
    this.appState.showView.next(
      new ShowRequest('tab', 'dialer call logs', JSON.stringify(queryForSeqId))
    );
    this.appState.notifyInfo('Call log tab opened (close dialog to view).',this,2.5)
  }

  bExportingCalls = false;
  exportCalls() {
    console.log(`exportCalls()`);
    const rootTest = this.rootTest;
    const callTests = this.dialerTestService.flattenTest(rootTest).filter(test=>test.type=='VoiceCallTest') as VoiceCallTest[]

    this.bExportingCalls = true;
    const CsvRow = {
      name: '',
      id: '',
      phoneMac: '',
      phoneName: '',
      carrier: '',
      signalStrength: 0,
      networkStatus: '',

      callPath: '',
      index: 0,

      //props
      lineIdentification: '',
      startTime: '',
      multiparty: false,
      remoteHeld: false,
      remoteMultiparty: false,
      emergency: false,

      disconnectReason: '',
      duration: 0,

      dialingTime: '',
      dialingDuration: 0,
      alertingTime: '',
      alertingDuration: 0,
      activeTime: '',
      activeDuration: 0,
      disconnectedTime: '',
      disconnectedDuration: 0,
      reason: ''
    }
    try {
      const csvRows = [];
      const csvColNames = Object.keys(CsvRow);
      csvRows.push( csvColNames );
      const replacer = (key, value) => (value === null ? "" : value);

      for( let i = 0; i < callTests.length; i++ ) {
        const callTest = callTests[i];
        const call = callTest.call;
        if ( !call ) {
          continue;
        }
        //TODO - could add more transitions
        //export type CallStateName = 'INCOMING' | 'HELD' | 'WAITING' | 'DIALING' | 'ALERTING' | 'ACTIVE' | 'DISCONNECTED' | 'REMOVED'

        const dialTransition = call.transitions.find(t=>t.state=='DIALING');
        const alertingTransition = call.transitions.find(t=>t.state=='ALERTING');
        const activeTransition = call.transitions.find(t=>t.state=='ACTIVE');
        const disconnectedTransition = call.transitions.find(t=>t.state=='DISCONNECTED');

        const csvRow = { ...CsvRow }
        csvRow.name = callTest.name;
        csvRow.id = call.id;
        csvRow.phoneMac = callTest.phoneMacAddress || callTest.phone?.mac;
        csvRow.phoneName = callTest.phone?.modemProps?.name || call['phoneName'];
        csvRow.carrier = callTest.phone?.network?.registration?.name;
        csvRow.signalStrength = callTest.phone?.network?.registration.strength;
        csvRow.networkStatus = callTest.phone?.network?.registration.status;

        csvRow.callPath = call.callPath;
        csvRow.index = callTest.index;
        csvRow.lineIdentification = call.props?.lineIdentification;
        csvRow.startTime = call.props?.startTime;
        csvRow.multiparty = call.props?.multiparty;
        csvRow.remoteHeld = call.props?.remoteHeld;
        csvRow.remoteMultiparty = call.props?.remoteMultiparty;
        csvRow.emergency = call.props?.emergency;

        csvRow.disconnectReason = call.disconnectReason;
        csvRow.duration = call.duration;

        csvRow.dialingTime = dialTransition?.time;
        csvRow.dialingDuration = dialTransition?.duration;
        csvRow.alertingTime = alertingTransition?.time;
        csvRow.alertingDuration = alertingTransition?.duration;
        csvRow.activeTime = activeTransition?.time;
        csvRow.activeDuration = activeTransition?.duration;
        csvRow.disconnectedTime = disconnectedTransition?.time;
        csvRow.disconnectedDuration = disconnectedTransition?.duration;

        csvRow.reason = callTest.reason;

        csvRows.push(csvColNames.map(col=>JSON.stringify(csvRow[col], replacer)));
      }
      let csvStrRows = csvRows.map(csvRow=>csvRow.join(','));
      let strCsv = csvStrRows.join('\r\n');
      var blob = new Blob([strCsv], {type: 'text/csv' })
      saveAs(blob, `bluetooth-calls-${rootTest.pointId||rootTest.routeId}-${rootTest.stage}-${rootTest?.cycle}.csv`);
    } catch ( error: any ) {
      console.error(error);
    }
    this.bExportingCalls = false
  }

  async reportIssue() {
    let testStartDate = new Date(this.rootTest.testStartTime);
    let adjustedStartDate = new Date(testStartDate.getTime()-5000);
    let adjustedStopDate = new Date(testStartDate.getTime() + 5000*(this.rootTest.testDuration + 5));

    const modalRef = DialerClientIssueModalComponent.showModal(this.bsModalService, {
      journalSince: this.datePipe.transform(adjustedStartDate,'yyyy-MM-dd HH:mm:ss.SSS UTC','UTC'),
      journalUntil: this.datePipe.transform(adjustedStopDate,'yyyy-MM-dd HH:mm:ss.SSS UTC','UTC'),
      testTitle: (this.rootTest.pointId || this.rootTest.routeId)?.replace(/-/g,'')
        + (this.rootTest.cycle?'_'+this.rootTest.cycle:'')
    });
    modalRef.content?.loadJournalCtlLog();
  }
}
